<template>
  <div>
    <!-- Header -->
    <div class="d-flex align-center justify-space-between pa-4 bg-background">
      <div class="d-flex align-center ga-3">
        <SoButton icon="arrow-left" type="icon" @click="$router.back()" />
        <SoChip
          :type="
            formData.status === 'published'
              ? SoChipType.SUCCESS
              : SoChipType.WARNING
          "
          :text="formData.status === 'published' ? 'Published' : 'Draft'"
        />
        <span v-if="lastSaved" class="text-body-2 text-medium-emphasis">
          Last saved {{ lastSaved }}
        </span>
      </div>
      <div class="d-flex ga-2">
        <SoButton
          title="Preview"
          prepend-icon="eye"
          @click="showPreview = true"
        />
        <SoButton
          :title="mode === 'create' ? 'Save Draft' : 'Save'"
          type="primary"
          prepend-icon="save"
          :loading="isSaving && !publishOnSave"
          :disabled="isSaveDisabled"
          @click="handleSubmit"
        />
        <SoButton
          v-if="formData.status === 'draft'"
          title="Publish"
          type="success"
          prepend-icon="send"
          :loading="isSaving && publishOnSave"
          :disabled="isSaveDisabled"
          @click="saveAndPublish"
        />
        <SoButton
          v-if="formData.status === 'published'"
          title="Unpublish"
          prepend-icon="eye-off"
          :loading="isUnpublishing"
          @click="unpublishArticle"
        />
        <SoButton
          v-if="mode === 'edit'"
          title="Delete"
          type="danger"
          prepend-icon="delete"
          :loading="isDeleting"
          @click="confirmDelete"
        />
      </div>
    </div>
    <SoDivider />

    <!-- Main Content -->
    <div class="d-flex justify-center pa-12 pb-16">
      <SoForm v-model="isFormValid" class="article-form" @submit="handleSubmit">
        <SoTextField
          v-model="formData.title"
          label="Title"
          placeholder="Untitled Article"
          :rules="[rules.required]"
          data-testid="article-title-input"
        />

        <div class="d-flex ga-4 mt-4">
          <SoTextField
            :model-value="authorName"
            label="Author"
            disabled
            class="flex-1-0"
          />
          <SoSelect
            v-model="formData.type"
            :items="typeOptions"
            label="Type"
            type="simple"
            class="flex-1-0"
            data-testid="article-type-select"
          />
        </div>

        <div class="mt-4">
          <SoTextEditor
            v-model="formData.body"
            :features="[
              'formatting',
              'link',
              'list',
              'align',
              'blockquote',
              'image',
            ]"
            :image-upload-handler="handleEditorImageUpload"
            data-testid="article-body-editor"
          />
        </div>
      </SoForm>
    </div>

    <!-- Footer -->
    <div class="article-footer">
      <SoDivider />
      <div class="d-flex justify-end pa-3">
        <span class="text-body-2 text-medium-emphasis"
          >Press Ctrl+S to save</span
        >
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <SoDialog v-model="showDeleteDialog" card-title="Delete Article">
      <SoAlert
        type="warning"
        text="Are you sure you want to delete this article? This action cannot be
        undone."
      />
      <div class="d-flex justify-end ga-2 mt-8">
        <SoButton title="Cancel" large @click="showDeleteDialog = false" />
        <SoButton
          type="danger"
          title="Delete"
          large
          :loading="isDeleting"
          @click="deleteArticle"
        />
      </div>
    </SoDialog>

    <!-- Preview Dialog -->
    <SoDialog v-model="showPreview" card-title="Preview">
      <h1 class="text-h4 mb-4">
        {{ formData.title || "Untitled Article" }}
      </h1>
      <SoDivider class="mb-4" />
      <div class="bulletpoint-list" v-html="formData.body" />
    </SoDialog>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { NewsfeedMutator } from "@/mutators/newsfeed/NewsfeedMutator"
  import { AuthService } from "@/services/AuthService"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { errorReporter } from "@/services/ErrorService"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoTextEditor from "@/components/SoTextEditor.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import rules from "@/validators/rules"
  import type {
    INewsfeedItem,
    ArticleType,
    IImageUploadResponse,
  } from "@/models/newsfeed/newsfeed.types"
  import type { IArticleRaw } from "@/models/newsfeed/newsfeed.parser"
  import type { CreateArticlePayload } from "@/mutators/newsfeed/NewsfeedMutator.types"

  const typeOptions = [
    { title: "Bug", value: "bug" },
    { title: "Incident", value: "incident" },
    { title: "Process", value: "process" },
    { title: "Information", value: "information" },
    { title: "Other", value: "other" },
  ]

  interface ArticleFormData {
    title: string
    body: string
    summary: string
    author: string
    type: ArticleType
    status: "draft" | "published"
  }

  export default defineComponent({
    components: {
      SoForm,
      SoButton,
      SoTextField,
      SoSelect,
      SoTextEditor,
      SoChip,
      SoDialog,
      SoDivider,
      SoAlert,
    },
    props: {
      mode: {
        type: String as PropType<"create" | "edit">,
        required: true,
      },
      article: {
        type: Object as PropType<INewsfeedItem>,
        default: null,
      },
    },
    emits: ["success", "deleted"],
    data() {
      return {
        SoChipType,
        rules,
        snackbar: useSnackbarStore(),
        typeOptions,
        isFormValid: false,
        isSaving: false,
        isDeleting: false,
        isUnpublishing: false,
        publishOnSave: false,
        showDeleteDialog: false,
        showPreview: false,
        lastSaved: null as string | null,
        imageUploadMutator: NewsfeedMutator.uploadImage(undefined, () =>
          useSnackbarStore().addError("Failed to upload image"),
        ),
        createArticleMutator: NewsfeedMutator.createArticle(),
        updateArticleMutator: NewsfeedMutator.updateArticle(),
        formData: {
          title: "",
          body: "",
          summary: "",
          author:
            AuthService.getCurrentUser()?.name ||
            AuthService.getCurrentUser()?.username ||
            "",
          type: "information" as ArticleType,
          status: "draft" as "draft" | "published",
        } as ArticleFormData,
      }
    },
    computed: {
      authorName(): string {
        return this.formData.author || "Unknown Author"
      },
      hasBodyContent(): boolean {
        if (!this.formData.body) return false

        // Check for images
        const hasImages = /<img\s[^>]*src=/i.test(this.formData.body)
        if (hasImages) return true

        // Strip HTML tags and check if there's actual text content
        const bodyText = this.formData.body.replaceAll(/<[^>]*>/g, "").trim()
        return !!bodyText
      },
      isSaveDisabled(): boolean {
        return !this.isFormValid || this.isSaving || !this.hasBodyContent
      },
    },
    watch: {
      article: {
        immediate: true,
        handler(article: INewsfeedItem | null) {
          if (article) {
            this.formData = {
              title: article.title,
              body: article.body,
              summary: article.summary || "",
              author: article.author || this.formData.author,
              type: (article.type as ArticleType) || "information",
              status: article.status || "draft",
            }
          }
        },
      },
    },
    mounted() {
      // Listen for Ctrl+S
      globalThis.addEventListener("keydown", this.handleKeyDown)
    },
    beforeUnmount() {
      globalThis.removeEventListener("keydown", this.handleKeyDown)
    },
    methods: {
      handleKeyDown(e: KeyboardEvent) {
        if ((e.ctrlKey || e.metaKey) && e.key === "s") {
          e.preventDefault()
          if (!this.isSaveDisabled) {
            this.handleSubmit()
          }
        }
      },
      async handleEditorImageUpload(file: File): Promise<string> {
        const response = await this.imageUploadMutator.mutateAsync(file)
        return (response as IImageUploadResponse).url
      },
      saveAndPublish() {
        this.publishOnSave = true
        this.formData.status = "published"
        this.handleSubmit()
      },
      async handleSubmit() {
        if (!this.formData.title) {
          this.snackbar.addError("Please add a title")
          return
        }

        this.isSaving = true

        const payload: CreateArticlePayload = {
          title: this.formData.title,
          body: this.formData.body,
          summary: this.formData.summary || undefined,
          author: this.formData.author,
          type: this.formData.type,
          status: this.formData.status,
        }

        try {
          if (this.mode === "create") {
            const result = await this.createArticleMutator.mutateAsync(payload)
            this.snackbar.addSuccess("Article created successfully")
            this.lastSaved = "just now"
            this.$emit("success", (result as IArticleRaw).id)
          } else if (this.article?.id) {
            await this.updateArticleMutator.mutateAsync({
              ...payload,
              id: this.article.id,
            })
            this.snackbar.addSuccess("Article saved")
            this.lastSaved = "just now"
            this.$emit("success")
          }
        } catch (error: unknown) {
          errorReporter.report(error)
          console.error("Article save error:", error)
          if (this.mode === "create") {
            this.snackbar.addError("Failed to create article")
          } else {
            this.snackbar.addError("Failed to update article")
          }
        } finally {
          this.isSaving = false
          this.publishOnSave = false
        }
      },
      async unpublishArticle() {
        if (!this.article?.id) return

        this.isUnpublishing = true
        try {
          const unpublishMutator = NewsfeedMutator.unpublishArticle(
            () => {
              this.formData.status = "draft"
              this.snackbar.addSuccess("Article unpublished")
            },
            () => this.snackbar.addError("Failed to unpublish article"),
          )
          await unpublishMutator.mutateAsync(this.article.id)
        } finally {
          this.isUnpublishing = false
        }
      },
      confirmDelete() {
        this.showDeleteDialog = true
      },
      async deleteArticle() {
        if (!this.article?.id) return

        this.isDeleting = true
        try {
          const deleteMutator = NewsfeedMutator.deleteArticle(
            () => {
              this.showDeleteDialog = false
              this.$emit("deleted")
            },
            () => this.snackbar.addError("Failed to delete article"),
          )
          await deleteMutator.mutateAsync(this.article.id)
        } finally {
          this.isDeleting = false
        }
      },
    },
  })
</script>

<style scoped>
  .article-form {
    width: 800px;
    max-width: 800px;
  }

  .article-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgb(var(--v-theme-surface));
  }
</style>
